import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import {
  EditPeriodisationDialogProps,
  EditPeriodisationFormType,
  EditPeriodisationRowFormValue,
} from "models/propTypes/invoicePropTypes";
import { PeriodisationMode } from "models/utilTypes/periodisationTypes";
//#endregion
//#region components
import { useWintForm, WintForm } from "components/common/wintForms";
import DimensionsDialogFilePortal from "components/common/dimensions/dimensionsDialogFilePortal";
import PeriodisationEditor from "./_fields/periodisationEditor";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { DateHelper } from "utils/helpers";

const buildRowFormValue = (row: WintSuperkollAPIModel.InvoiceRow): EditPeriodisationRowFormValue => {
  const hasDateFields = row.periodisationStartDate !== undefined && row.periodisationStartDate !== null;
  const mode = hasDateFields ? "date" : "month";
  const start = row.periodisation
    ? hasDateFields
      ? row.periodisationStartDate
        ? DateHelper.parseDateOnly(row.periodisationStartDate as unknown as string)
        : undefined
      : DateHelper.getDateFromYearMonth((row.periodisationStartYear ?? 0) * 100 + (row.periodisationStartMonth ?? 0))
    : undefined;
  const end = row.periodisation
    ? hasDateFields
      ? row.periodisationEndDate
        ? DateHelper.parseDateOnly(row.periodisationEndDate as unknown as string)
        : undefined
      : DateHelper.getDateFromYearMonth((row.periodisationEndYear ?? 0) * 100 + (row.periodisationEndMonth ?? 0))
    : undefined;
  return {
    invoiceItemId: row.id,
    text: row.overriddenDescription ?? row.description ?? "",
    periodisation: {
      mode,
      range: start || end ? { start, end } : undefined,
    },
  };
};

const isBlankSpacingRow = (row: WintSuperkollAPIModel.InvoiceRow): boolean => {
  if (row.periodisation) return false;
  const hasNoDescription = !row.overriddenDescription && !row.description;
  const hasNoPrice = !row.overriddenUnitPrice && !row.unitPrice;
  return (hasNoDescription && hasNoPrice);
};

const buildDefaultValues = (invoice: WintSuperkollAPIModel.Invoice): EditPeriodisationFormType => {
  const rawRows = (invoice.rows ?? []).filter((row) => !isBlankSpacingRow(row)).map(buildRowFormValue);
  const isPeriodisationByDate = rawRows.some((row) => row.periodisation.mode === "date");
  const mode: PeriodisationMode = isPeriodisationByDate ? "date" : "month";
  const rows = rawRows.map((row) => ({ ...row, periodisation: { ...row.periodisation, mode } }));
  const firstRange = rows[0]?.periodisation.range;
  const isAdvanced = rows.some((row) => {
    return (
      row.periodisation.range?.start?.getTime() !== firstRange?.start?.getTime() ||
      row.periodisation.range?.end?.getTime() !== firstRange?.end?.getTime()
    );
  });
  return {
    rows,
    isPeriodisationAdvancedConfiguration: isAdvanced,
    isPeriodisationByDate,
  };
};

const toRowPut = (row: EditPeriodisationRowFormValue): WintSuperkollAPIModel.InvoicePeriodisationRowPut => {
  const { invoiceItemId, periodisation } = row;
  const range = periodisation.range;
  if (!range || (!range.start && !range.end)) {
    return { invoiceItemId, periodisation: false };
  }
  if (periodisation.mode === "date") {
    return {
      invoiceItemId,
      periodisation: true,
      periodisationStartDate: range.start as unknown as any,
      periodisationEndDate: range.end as unknown as any,
    };
  }
  return {
    invoiceItemId,
    periodisation: true,
    periodisationStartYear: range.start ? DateHelper.getYear(range.start) : undefined,
    periodisationStartMonth: range.start ? DateHelper.getMonth(range.start) : undefined,
    periodisationEndYear: range.end ? DateHelper.getYear(range.end) : undefined,
    periodisationEndMonth: range.end ? DateHelper.getMonth(range.end) : undefined,
  };
};

const isRowPeriodisationLocked = (row: WintSuperkollAPIModel.InvoiceRow, lockDate?: Date): boolean => {
  if (!lockDate || !row.periodisation) return false;
  const hasDateFields = row.periodisationStartDate !== undefined && row.periodisationStartDate !== null;
  if (hasDateFields) {
    const start = row.periodisationStartDate
      ? DateHelper.parseDateOnly(row.periodisationStartDate as unknown as string)
      : undefined;
    const end = row.periodisationEndDate
      ? DateHelper.parseDateOnly(row.periodisationEndDate as unknown as string)
      : undefined;
    return (!!start && start <= lockDate) || (!!end && end <= lockDate);
  }
  const start =
    row.periodisationStartYear && row.periodisationStartMonth
      ? DateHelper.getDateFromYearMonth(row.periodisationStartYear * 100 + row.periodisationStartMonth)
      : undefined;
  const end =
    row.periodisationEndYear && row.periodisationEndMonth
      ? DateHelper.getLastDayOfMonth(
          DateHelper.getDateFromYearMonth(row.periodisationEndYear * 100 + row.periodisationEndMonth)
        )
      : undefined;
  return (!!start && start <= lockDate) || (!!end && end <= lockDate);
};

const EditPeriodisationDialog: ForwardRefRenderFunction<FormHandles, EditPeriodisationDialogProps> = (props, ref) => {
  const { invoice, reloadInvoice, onFormValidationChange, fileViewer } = props;
  const invoiceService = useInvoiceService();
  const lockDate = useSelector((store: AppState) => store.auth.selectedCompany.lockDate);

  const lockedInvoiceItemIds = React.useMemo(() => {
    const ids = new Set<number>();
    (invoice.rows ?? []).forEach((row) => {
      if (isRowPeriodisationLocked(row, lockDate)) ids.add(row.id);
    });
    return ids;
  }, [invoice, lockDate]);

  const handleSubmit = async (values: EditPeriodisationFormType) => {
    const body: WintSuperkollAPIModel.InvoicePeriodisationsPut = {
      rows: values.rows.map(toRowPut),
    };
    await invoiceService.editPeriodisations(invoice.id, body);
    reloadInvoice && reloadInvoice();
  };

  // The portal target lives in the dialog shell and exists only after this component has mounted.
  const [filePortalKey, setFilePortalKey] = React.useState(0);
  React.useEffect(() => setFilePortalKey((key) => key + 1), []);

  const { wintFormProps, connect, values, setValues } = useWintForm<EditPeriodisationFormType>({
    formRef: ref,
    defaultValues: buildDefaultValues(invoice),
    handleSubmit,
    onFormValidationChange,
    enableReinitialize: false,
  });

  return (
    <>
      {fileViewer && <DimensionsDialogFilePortal key={filePortalKey}>{fileViewer}</DimensionsDialogFilePortal>}
      <WintForm {...wintFormProps}>
        <PeriodisationEditor
          values={values}
          connect={connect}
          setValues={setValues}
          lockedInvoiceItemIds={lockedInvoiceItemIds}
        />
      </WintForm>
    </>
  );
};

export default forwardRef(EditPeriodisationDialog);
